import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Instagram, Quote, Star } from "lucide-react";
import { hexA } from "@/lib/storeutils";

/**
 * Vendor-managed homepage sections (customer reviews and Instagram posts) shared by EVERY theme.
 * Content comes from the `homepage` contract in /api/store/{slug}; themes only choose a `skin` (look).
 * Everything else on a homepage is the theme's own automatic design.
 */

const NAVY = "var(--m-navy, #1d2b6b)";
const SKINS = {
  retail:   { cat: "rounded", head: "eyebrow", rail: "rounded-2xl" },
  pharmacy: { cat: "card",    head: "center",  rail: "rounded-md" },
  medi:     { cat: "circle",  head: "centerNavy", rail: "rounded-sm" },
  books:    { cat: "rounded", head: "serif",   rail: "rounded-2xl" },
  bookish:  { cat: "rounded", head: "serifLine", rail: "rounded-none" },
  verdant:  { cat: "rounded", head: "serif",   rail: "rounded-3xl" },
  aroma:    { cat: "rounded", head: "aroma",   rail: "rounded-none" },
  market:   { cat: "rounded", head: "market",  rail: "rounded-xl" },
  artisan:  { cat: "rounded", head: "artisan", rail: "rounded-3xl" },
};
export const skinOf = (layout) => (layout === "pharmacy" ? "pharmacy" : layout === "medi" ? "medi" : layout === "books" ? "books" : layout === "bookish" ? "bookish" : layout === "verdant" ? "verdant" : layout === "aroma" ? "aroma" : layout === "market" ? "market" : layout === "artisan" ? "artisan" : "retail");

export function readHomepage(s) {
  const hp = s.homepage || {};
  return { configured: !!hp.configured, sections: hp.sections || {}, serverTime: hp.server_time };
}

/** Mounts children when the section nears the viewport (keeps the initial homepage render light). */
function Lazy({ children, minHeight = 240, id }) {
  const ref = useRef(null);
  const [show, setShow] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (show) return undefined;
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  if (show) return children;
  return <div ref={ref} id={id} style={{ minHeight }} aria-hidden />;
}

const EYEBROW = { categories: "Top Categories", deal: "Limited Time Offer", new_arrivals: "Just In", best_sellers: "Customer Favourites", featured: "Handpicked For You", reviews: "Testimonials", instagram: "Follow Us" };

function Heading({ skin, c, font, title, eyebrow, right }) {
  const k = SKINS[skin].head;
  if (k === "eyebrow") {
    return (
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest" style={{ color: c.accent }}>{eyebrow}</div>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: font }}>{title}</h2>
        </div>
        {right}
      </div>
    );
  }
  if (k === "serifLine") {
    return (
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[26px] md:text-[28px] font-bold leading-tight" style={{ fontFamily: "'Merriweather', Georgia, serif", color: "#2a2a2a" }}>{title}</h2>
          <span className="block mt-3 h-[2px] w-[54px]" style={{ background: c.primary }} />
        </div>
        {right}
      </div>
    );
  }
  if (k === "artisan") {
    return (
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 text-[11px] uppercase" style={{ letterSpacing: ".3em", color: "var(--z-primary, #8c2f39)" }}><span className="h-px w-8" style={{ background: "currentColor" }} />{eyebrow}<span className="h-px w-8" style={{ background: "currentColor" }} /></div>
        <h2 className="mt-3 text-[28px] md:text-[38px] leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500, color: "#2a1d1a" }}>{title}</h2>
        {right}
      </div>
    );
  }
  if (k === "market") {
    return (
      <div className="flex items-center gap-3">
        <h2 className="text-[20px] md:text-[22px] font-bold whitespace-nowrap" style={{ color: "#1c2a24" }}>{title}</h2>
        <span className="h-[2px] w-12" style={{ background: "var(--k-primary, #1b8a5a)" }} />
        <span className="flex-1" />{right}
      </div>
    );
  }
  if (k === "aroma") {
    return (
      <div className="text-center">
        <div className="text-[11px] uppercase" style={{ letterSpacing: ".3em", color: "#8a7f74" }}>{eyebrow}</div>
        <h2 className="mt-2 text-[28px] md:text-[32px] leading-tight" style={{ fontFamily: "'Marcellus', Georgia, serif", color: "#2b2420" }}>{title}</h2>
        {right}
      </div>
    );
  }
  if (k === "serif") {
    return (
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-[30px] md:text-[34px] leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400, color: "#1b1b18" }}>{title}</h2>
        {right}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center gap-3 flex-wrap">
      <h2 className={k === "center" ? "text-[26px] font-medium text-center" : "text-[28px] font-semibold text-center"} style={k === "centerNavy" ? { color: NAVY } : { color: "#111" }}>{title}</h2>
      {right}
    </div>
  );
}

const wrap = "max-w-6xl mx-auto px-5";
const productsById = (s) => Object.fromEntries(s.products.map((p) => [p.id, p]));

function ExtLink({ href, className, style, children, testId }) {
  if (href?.startsWith("/")) return <Link to={href} className={className} style={style} data-testid={testId}>{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style} data-testid={testId}>{children}</a>;
}

/* ------------------------------------------------------------------ Reviews */
export function ReviewsSection({ s, skin, data }) {
  const { c, font, border, muted, cardShadow } = s;
  const rail = useRef(null);
  if (!data?.items?.length) return null;
  const items = data.items;
  const carousel = items.length > 3;
  const go = (d) => rail.current?.scrollBy({ left: d * 340, behavior: "smooth" });
  const card = (r) => (
    <div className={`${SKINS[skin].rail} p-6 h-full flex flex-col`} style={{ background: skin === "retail" ? c.surface : "#fff", border: `1px solid ${skin === "retail" ? border : "#eef0f6"}`, boxShadow: cardShadow }} data-testid={`home-review-${r.id}`}>
      <Quote size={22} style={{ color: c.accent }} />
      <p className="mt-3 text-sm flex-1" style={{ color: muted }}>{r.message}</p>
      <div className="flex items-center gap-1 mt-4" aria-label={`${r.rating} out of 5 stars`}>{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} fill={n <= r.rating ? c.accent : "none"} color={n <= r.rating ? c.accent : "#cbd5e1"} />)}</div>
      <div className="mt-3 flex items-center gap-2.5">
        {r.image ? <img src={r.image} alt="" loading="lazy" decoding="async" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: hexA(c.primary, 0.12), color: c.primary }}>{r.name[0].toUpperCase()}</span>}
        <span className="font-bold text-sm" style={{ fontFamily: font }}>{r.name}</span>
      </div>
    </div>
  );
  return (
    <Lazy minHeight={260} id="reviews">
      <section id="reviews" className={`${wrap} py-10`} data-testid="home-reviews">
        <Heading skin={skin} c={c} font={font} title={data.title} eyebrow={EYEBROW.reviews} />
        {carousel ? (
          <div className="relative mt-6">
            <button type="button" onClick={() => go(-1)} aria-label="Previous reviews" className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform"><ChevronLeft size={16} /></button>
            <button type="button" onClick={() => go(1)} aria-label="Next reviews" className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform"><ChevronRight size={16} /></button>
            <div ref={rail} className="flex gap-5 overflow-x-auto scroll-smooth pb-3" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
              {items.map((r) => <div key={r.id} className="shrink-0 w-[300px] sm:w-[320px]" style={{ scrollSnapAlign: "start" }}>{card(r)}</div>)}
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5 mt-6">{items.map((r) => <div key={r.id}>{card(r)}</div>)}</div>
        )}
      </section>
    </Lazy>
  );
}

/* ---------------------------------------------------------------- Instagram */
export function InstagramSection({ s, skin, data }) {
  const { c, font } = s;
  if (!data?.items?.length) return null;
  return (
    <Lazy minHeight={220} id="instagram">
      <section id="instagram" className={`${wrap} py-10`} data-testid="home-instagram">
        <Heading skin={skin} c={c} font={font} title={data.title} eyebrow={EYEBROW.instagram} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
          {data.items.map((p) => (
            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className={`group relative aspect-square overflow-hidden ${skin === "retail" ? "rounded-2xl" : skin === "medi" ? "rounded-md" : "rounded-sm"}`} data-testid={`home-instagram-${p.id}`} aria-label={p.caption || "View on Instagram"}>
              <img src={p.image} alt={p.caption || "Instagram post"} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-2 text-center text-white opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,.5)" }}>
                <Instagram size={22} />
                {p.caption && <span className="text-[11px] leading-tight line-clamp-3">{p.caption}</span>}
              </span>
            </a>
          ))}
        </div>
      </section>
    </Lazy>
  );
}

/**
 * The two vendor-managed sections. A theme renders these in the slots where it shows reviews / Instagram:
 * the vendor's content when there is any, otherwise the theme's own default (`fallback`).
 */
export function ManagedReviews({ s, layout, fallback = null }) {
  const data = readHomepage(s).sections.reviews;
  return data?.items?.length ? <ReviewsSection s={s} skin={skinOf(layout)} data={data} /> : fallback;
}

export function ManagedInstagram({ s, layout, fallback = null }) {
  const data = readHomepage(s).sections.instagram;
  return data?.items?.length ? <InstagramSection s={s} skin={skinOf(layout)} data={data} /> : fallback;
}
